import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from 'reactstrap';
import { Tooltip } from 'primereact/tooltip';
import axios from 'axios';

const DEFAULT_URLS = ['/healthcheck'];

const makeEmptyWindow = (n) =>
  Array.from({ length: n }, () => ({ ms: -1, timeout: false }));

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const ComponentReload = ({
  urls = DEFAULT_URLS,
  goodMs = 300,
  warnMs = 500,
  windowSize = 20,
  minIntervalOk = 30000,
  maxIntervalOk = 10000,
  retryInterval = 3000,
  requestTimeoutMs = 2000,
  onClick,

  // warmup: solo la ÚLTIMA lectura se guarda (por defecto, la 3ra)
  warmupCount = 3,
  warmupGapMs = 80
}) => {
  // samples: tamaño fijo windowSize
  // - ms === -1 => aún no hay lectura (NO se evalúa)
  // - timeout === true => fue timeout (cuenta como pérdida y ms=requestTimeoutMs)
  const [samples, setSamples] = useState(() => makeEmptyWindow(windowSize));

  const [avg, setAvg] = useState(0);
  const [loss, setLoss] = useState(0);
  const [color, setColor] = useState('#9e9e9e');
  const [statusMessage, setStatusMessage] = useState('Analizando conexión...');

  const urlsRef = useRef(urls);
  const avgRef = useRef(0);
  const posRef = useRef(0);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  // 👇 Warmup solo una vez
  const didWarmupRef = useRef(false);

  // Mantener refs actualizadas sin reiniciar el loop
  useEffect(() => {
    urlsRef.current = urls;
  }, [urls]);

  useEffect(() => {
    avgRef.current = avg;
  }, [avg]);

  // Si cambia windowSize en runtime, rearmar buffer
  useEffect(() => {
    setSamples(makeEmptyWindow(windowSize));
  }, [windowSize]);

  const computeAndSet = (arr) => {
    const done = arr.filter((s) => s.ms !== -1); // solo lecturas realizadas
    const total = done.length;

    if (!total) {
      setAvg(0);
      setLoss(0);
      setColor('#9e9e9e');
      setStatusMessage('Analizando conexión...');
      return;
    }

    const timeouts = done.filter((s) => s.timeout).length;
    const newLoss = timeouts / total;

    const newAvg = done.reduce((a, s) => a + s.ms, 0) / total;

    setLoss(newLoss);
    setAvg(newAvg);

    if (newLoss >= 0.5) {
      setColor('#dc3545');
      setStatusMessage('Conexión inestable');
    } else if (newAvg <= goodMs) {
      setColor('#1b8433');
      setStatusMessage('Conexión excelente');
    } else if (newAvg <= warnMs) {
      setColor('#ffc107');
      setStatusMessage('Conexión aceptable');
    } else {
      setColor('#dc3545');
      setStatusMessage('Alta latencia');
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    const doRequestOnce = async (url, { record }) => {
      const start = performance.now();

      try {
        await axios.head(url, {
          timeout: requestTimeoutMs,
          headers: { 'Cache-Control': 'no-cache' },
          params: { _t: Date.now() }
        });

        const ms = Math.round(performance.now() - start);

        if (record) {
          setSamples((prev) => {
            const updated = [{ ms, timeout: false }, ...prev].slice(0, windowSize);
            computeAndSet(updated);
            return updated;
          });
        }

        return { ok: true };
      } catch {
        if (record) {
          setSamples((prev) => {
            const updated = [{ ms: requestTimeoutMs, timeout: true }, ...prev].slice(0, windowSize);
            computeAndSet(updated);
            return updated;
          });
        }

        return { ok: false };
      }
    };

    const ping = async () => {
      if (!mountedRef.current) return;

      const list = urlsRef.current?.length ? urlsRef.current : DEFAULT_URLS;

      const nextUrl = () => {
        const url = list[posRef.current];
        posRef.current = (posRef.current + 1) % list.length;
        return url;
      };

      // ✅ Warmup SOLO una vez: solo la última lectura se guarda
      if (!didWarmupRef.current) {
        didWarmupRef.current = true;

        const n = Math.max(1, warmupCount);

        for (let i = 0; i < n; i++) {
          if (!mountedRef.current) return;

          const url = nextUrl();
          const isLast = i === n - 1;

          const res = await doRequestOnce(url, { record: isLast });

          // si falla en warmup, reintenta rápido
          if (!res.ok) {
            timerRef.current = setTimeout(ping, retryInterval);
            return;
          }

          if (i < n - 1 && warmupGapMs > 0) {
            await sleep(warmupGapMs);
          }
        }
      } else {
        // Normal: 1 lectura por ciclo (sí se registra)
        const url = nextUrl();
        const res = await doRequestOnce(url, { record: true });

        // si falla, reintenta rápido
        if (!res.ok) {
          timerRef.current = setTimeout(ping, retryInterval);
          return;
        }
      }

      // schedule normal
      const delay = avgRef.current <= goodMs ? maxIntervalOk : minIntervalOk;
      timerRef.current = setTimeout(ping, delay);
    };

    ping();

    return () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doneCount = useMemo(
    () => samples.filter((s) => s.ms !== -1).length,
    [samples]
  );

  const avgText = doneCount ? `${Math.round(avg)} ms` : '—';
  const lossText = doneCount ? `${Math.round(loss * 100)}%` : '—';

  return (
    <>
      <Tooltip target=".net-health-btn" position="bottom" />

      <Button
        className="net-health-btn"
        color="link"
        onClick={onClick}
        data-pr-tooltip={`
Estado: ${statusMessage}
Promedio: ${avgText}
Pérdida: ${lossText}
Muestras: ${doneCount}/${windowSize}
  `}
        style={{ padding: 6 }}
      >
        <i className="pi pi-globe" style={{ fontSize: 20, color }} />
      </Button>
    </>
  );
};
